import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse } from 'src/app/models/api-response.model';
import {
  GeneratedNote,
  GenerateNoteApiData,
  GenerateNotePayload,
  GuideFormResponse,
  GuideRole,
} from 'src/app/models/care-call-guide.model';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class GenerateNoteService {
  private readonly baseUrl = `${environment.welbyEndpoint}/api/v1/ai/generate-care-call-note`;

  constructor(private http: HttpClient) {}

  /**
   * Generates the care call note and returns it WITH its integrity signals, so the caller can
   * refuse to present an incomplete note as complete. Dropping the flags here (`map(res =>
   * res.data.note)`) made a truncated or empty note indistinguishable from a good one.
   *
   * Tolerant of a backend that predates welby-micro-service#905: `truncated` cannot be detected
   * client-side so it defaults to `false`, while `empty` is derived from the note itself, keeping
   * the empty-note guard working against either backend version.
   *
   * `role` is passed in rather than read off the auth service: it is resolved from the user's
   * clinical licensure on the PATIENT's care team (see resolveCareCallRole), and this service has
   * no patient. The caller does.
   */
  generateCareCallNote(
    diagnosis: string,
    role: GuideRole,
    formResponses: GuideFormResponse,
    fieldLabels: Record<string, string> = {},
    escalations: string[] = [],
  ): Observable<GeneratedNote> {
    const payload: GenerateNotePayload = { diagnosis, role, formResponses, fieldLabels, escalations };
    return this.http.post<ApiResponse<GenerateNoteApiData>>(this.baseUrl, payload).pipe(
      map((res) => {
        const note = res.data?.note ?? '';
        return { note, truncated: res.data?.truncated ?? false, empty: res.data?.empty ?? !note.trim() };
      }),
    );
  }
}
